import { useResetOnClose } from "@/lib/useResetOnClose";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Send, History, Plus, Loader2, Trash2, ArrowRightCircle, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ItemPicker } from "@/components/procurement/ItemPicker";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

const PHASES = [
  { value: "RFQ", label: "RFQ sent", field: "rfq_date" },
  { value: "ORDERED", label: "Ordered", field: "actual_order_date" },
  { value: "MFC", label: "MFC confirmed", field: "actual_mfc_date" },
  { value: "INSPECTED", label: "Inspected", field: "inspection_date" },
  { value: "DISPATCHED", label: "Dispatched", field: "dispatch_date" },
  { value: "DELIVERED", label: "Delivered", field: "actual_delivery_date" },
];
const AUTO_PHASES = new Set(["RFQ", "ORDERED", "DELIVERED"]); // filled automatically once linked to a real RFQ/PO/GRN

export default function Materials() {
  const { projectId, canWrite } = useProjectCtx();
  const canEdit = canWrite("PROCUREMENT");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [updating, setUpdating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/materials`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  const leafItems = items.filter((i) => !i.is_group);

  return (
    <div className="space-y-4" data-testid="materials-page">
      <PageHeader
        title="Materials — Procurement Status"
        description={canEdit ? "RFQ, order and delivery dates fill in automatically once linked to real Procurement documents." : "Read-only for your project role."}
        action={canEdit && (
          <Button onClick={() => setUpdating(true)} data-testid="material-update-button">
            <Send className="h-4 w-4" /> Update
          </Button>
        )}
      />
      <Tabs defaultValue="materials" data-testid="materials-tabs">
        <TabsList>
          <TabsTrigger value="materials" data-testid="materials-tab-materials">Materials</TabsTrigger>
          <TabsTrigger value="indents" data-testid="materials-tab-indents"><ClipboardList className="mr-1.5 h-3.5 w-3.5" /> Purchase Indents</TabsTrigger>
          <TabsTrigger value="log" data-testid="materials-tab-log"><History className="mr-1.5 h-3.5 w-3.5" /> Update Log</TabsTrigger>
        </TabsList>

        <TabsContent value="materials" className="space-y-4">
          <div className="rounded-xl border border-border bg-card">
            {!loading && leafItems.length === 0 ? (
              <EmptyState title="No procurement items" testId="materials-empty-state" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Code</TableHead><TableHead>Material</TableHead><TableHead>Supplier</TableHead><TableHead>Planned delivery</TableHead><TableHead>Actual delivery</TableHead><TableHead>Status</TableHead><TableHead>Received</TableHead><TableHead></TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {leafItems.map((i) => (
                    <TableRow key={i.id} data-testid={`material-row-${i.id}`}>
                      <TableCell className="font-mono text-xs">{i.code}</TableCell>
                      <TableCell className="text-foreground">{i.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{i.material?.supplier || "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.material?.planned_delivery_date)}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.material?.actual_delivery_date)}</TableCell>
                      <TableCell><StatusPill value={i.material?.current_status} /></TableCell>
                      <TableCell className="font-mono text-xs">{fmtNum(i.material?.qty_received)} / {fmtNum(i.material?.qty_ordered)}{Number(i.material?.qty_ordered || 0) > Number(i.material?.qty_received || 0) && <div className="text-[10px] font-semibold text-amber-600 dark:text-amber-400" title="Ordered on approved POs but not yet received on a posted GRN — see Purchase Orders > Awaiting delivery">{fmtNum(Number(i.material.qty_ordered) - Number(i.material?.qty_received || 0))} pending</div>}</TableCell>
                      <TableCell className="text-right">
                        {canEdit && (
                          <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`edit-material-btn-${i.id}`} title="Full edit"><Pencil className="h-3.5 w-3.5" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="indents">
          <MaterialIndents projectId={projectId} canEdit={canEdit} leafItems={leafItems} />
        </TabsContent>

        <TabsContent value="log">
          <UpdateLog projectId={projectId} />
        </TabsContent>
      </Tabs>
      {editing && <MaterialDialog projectId={projectId} item={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {updating && <QuickUpdateDialog projectId={projectId} items={leafItems} onClose={() => setUpdating(false)} onSaved={load} />}
    </div>
  );
}

function UpdateLog({ projectId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/progress-entries`, { params: { category: "PROCUREMENT", limit: 100 } });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const METRIC_LABELS = { DOC_STATUS: "Status", STAGE_DATE: "Date", SCHEDULE_DATE: "Date", QUANTITY_CUMULATIVE: "Qty received", MATERIAL_STATUS: "Status" };

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="materials-update-log">
      {!loading && rows.length === 0 ? (
        <EmptyState title="No updates yet" description="Every Update you make — or that syncs in automatically from Procurement — will show up here." testId="materials-log-empty-state" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow><TableHead>Date</TableHead><TableHead>Material</TableHead><TableHead>What changed</TableHead><TableHead>Before → After</TableHead><TableHead>Updated by</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const fieldLabel = r.sub_stage
                ? r.sub_stage.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())
                : METRIC_LABELS[r.metric] || r.metric.replaceAll("_", " ");
              const after = r.value_date ? fmtDate(r.value_date) : r.status_value ?? (r.quantity !== null ? fmtNum(r.quantity) : "—");
              const before = r.previous_value || "—";
              return (
                <TableRow key={r.id} data-testid={`materials-log-row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(r.entry_date)}</TableCell>
                  <TableCell className="text-xs">
                    {r.item_code ? <span className="font-mono text-foreground">{r.item_code}</span> : "—"}
                    {r.item_name && <div className="text-[10px] text-muted-foreground">{r.item_name}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fieldLabel}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <span className="text-muted-foreground">{before}</span> <span className="text-muted-foreground">→</span> <span className="font-semibold text-foreground">{after}</span>
                  </TableCell>
                  <TableCell className="text-xs">{r.entered_by_name || <span className="italic text-muted-foreground">Unknown</span>}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function QuickUpdateDialog({ projectId, items, onClose, onSaved }) {
  const [itemId, setItemId] = useState("");
  const [phase, setPhase] = useState("");
  const [entryDate, setEntryDate] = useState("");
  const [dateTouched, setDateTouched] = useState(false);
  const [orderNo, setOrderNo] = useState("");
  const [qtyReceived, setQtyReceived] = useState("");
  const [saving, setSaving] = useState(false);
  const item = items.find((i) => i.id === itemId);
  const phaseDef = PHASES.find((p) => p.value === phase);

  const chooseItem = (v) => {
    setItemId(v);
    setPhase("");
  };
  const choosePhase = (v) => {
    setPhase(v);
    if (!dateTouched) setEntryDate(todayISO());
    if (v === "ORDERED") setOrderNo(item?.material?.order_no || "");
    if (v === "DELIVERED") setQtyReceived(item?.material?.qty_received != null ? String(item.material.qty_received) : "");
  };
  const changeDate = (e) => {
    setEntryDate(e.target.value);
    setDateTouched(true);
  };

  const submit = async () => {
    if (!item || !phaseDef || !entryDate) return;
    setSaving(true);
    const payload = { [phaseDef.field]: entryDate, entry_date: entryDate };
    if (phase === "ORDERED" && orderNo) payload.order_no = orderNo;
    if (phase === "DELIVERED" && qtyReceived !== "") payload.qty_received = Number(qtyReceived);
    try {
      await api.patch(`/projects/${projectId}/materials/${item.id}`, payload);
      toast.success(`${phaseDef.label} recorded`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="material-quick-update-dialog">
        <DialogHeader><DialogTitle>Update today's progress</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Material</Label>
            <Select value={itemId} onValueChange={chooseItem}>
              <SelectTrigger data-testid="material-update-item-select" className="mt-1.5"><SelectValue placeholder="Choose a material" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {item && (
            <div>
              <Label>Phase</Label>
              <Select value={phase} onValueChange={choosePhase}>
                <SelectTrigger data-testid="material-update-phase-select" className="mt-1.5"><SelectValue placeholder="Choose a phase" /></SelectTrigger>
                <SelectContent>
                  {PHASES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}{AUTO_PHASES.has(p.value) ? " (usually automatic)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {phase && AUTO_PHASES.has(phase) && (
                <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                  This date normally fills in on its own once the material is linked to a real RFQ/PO/GRN in Procurement. Only set it by hand to correct something.
                </p>
              )}
            </div>
          )}
          {item && phaseDef && (
            <div>
              <Label>{phaseDef.label} date</Label>
              <Input type="date" value={entryDate} onChange={changeDate} className="mt-1.5" data-testid="material-update-date-input" />
              <p className="mt-1 text-[11px] text-muted-foreground">Filled in automatically with today's date — change it if this happened on a different day.</p>
            </div>
          )}
          {item && phase === "ORDERED" && (
            <div><Label>Order no. (optional)</Label><Input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} className="mt-1.5" data-testid="material-update-order-no" /></div>
          )}
          {item && phase === "DELIVERED" && (
            <div><Label>Qty received (optional)</Label><Input type="number" value={qtyReceived} onChange={(e) => setQtyReceived(e.target.value)} className="mt-1.5" data-testid="material-update-qty-received" /></div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!item || !phaseDef || !entryDate || saving} data-testid="material-update-submit-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MaterialIndents({ projectId, canEdit, leafItems }) {
  const { companies } = useCompany() || {};
  const { project } = useProjectCtx();
  const [indents, setIndents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/procurement/indents");
      setIndents((data || []).filter((i) => i.project_id === projectId));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || "—";
  const materialName = (wbsItemId) => {
    const it = leafItems.find((i) => i.id === wbsItemId);
    return it ? `${it.code} — ${it.name}` : null;
  };

  const submitIndent = async (indent) => {
    setBusyId(indent.id);
    try {
      const { data } = await api.post(`/procurement/indents/${indent.id}/submit`);
      toast.success(data?.status === "CONVERTED" ? "Indent approved — a Purchase Requisition was created automatically" : "Indent submitted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };
  const convertIndent = async (indent) => {
    setBusyId(indent.id);
    try {
      await api.post(`/procurement/indents/${indent.id}/convert-to-requisition`, { requisition_date: todayISO() });
      toast.success("Converted to purchase requisition");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="material-indents-tab">
      <div className="flex justify-end">
        {canEdit && <RaiseIndentDialog projectId={projectId} companies={companies} defaultCompanyId={project?.company_id} leafItems={leafItems} onCreated={load} />}
      </div>
      {!loading && indents.length === 0 ? (
        <EmptyState title="No purchase indents for this project yet" description="Raise one for a material that needs to be bought." testId="material-indents-empty-state" />
      ) : (
        <div className="space-y-3">
          {indents.map((indent) => (
            <div key={indent.id} className="rounded-xl border border-border bg-card p-4" data-testid={`material-indent-card-${indent.number}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-xs text-muted-foreground">{indent.number}</p>
                  <p className="text-sm text-foreground">{companyName(indent.company_id)} · {indent.indent_date}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill value={indent.status} />
                  {canEdit && indent.status === "DRAFT" && (
                    <Button size="sm" variant="outline" disabled={busyId === indent.id} onClick={() => submitIndent(indent)} data-testid={`submit-material-indent-${indent.number}`}>
                      <Send className="h-3.5 w-3.5" /> Submit
                    </Button>
                  )}
                  {canEdit && indent.status === "APPROVED" && (
                    <Button size="sm" variant="outline" disabled={busyId === indent.id} onClick={() => convertIndent(indent)} data-testid={`convert-material-indent-${indent.number}`}>
                      <ArrowRightCircle className="h-3.5 w-3.5" /> Convert to PR
                    </Button>
                  )}
                </div>
              </div>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {indent.items.map((line) => (
                  <li key={line.id}>
                    {line.description} — {line.quantity} @ {line.estimated_rate || "—"}
                    {materialName(line.wbs_item_id) && <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-foreground">{materialName(line.wbs_item_id)}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_INDENT_ITEM = { description: "", quantity: "", estimated_rate: "", wbs_item_id: "" };

function RaiseIndentDialog({ projectId, companies, defaultCompanyId, leafItems, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: defaultCompanyId || "", indent_date: todayISO(), remarks: "" });
  const [items, setItems] = useState([{ ...EMPTY_INDENT_ITEM }]);
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({ company_id: defaultCompanyId || "", indent_date: todayISO(), remarks: "" }); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  const pickItem = (idx, item) => setItems(items.map((it, i) => (i === idx ? {
    ...it, item_id: item.id, description: item.name, uom_id: item.uom_id || it.uom_id || null, estimated_rate: Number(item.standard_rate) > 0 ? String(item.standard_rate) : it.estimated_rate,
  } : it)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/indents", {
        ...form, project_id: projectId,
        items: items.map((it) => ({ ...it, estimated_rate: it.estimated_rate || null, wbs_item_id: it.wbs_item_id || null })),
      });
      toast.success("Purchase indent raised");
      setItems([{ ...EMPTY_INDENT_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="raise-material-indent-button"><Plus className="h-4 w-4" /> Raise Indent</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Raise Purchase Indent — this project</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="raise-material-indent-form">
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="material-indent-company-select" />
            <div><Label>Date</Label><Input required type="date" value={form.indent_date} onChange={set("indent_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <p className="mt-1 text-[11px] text-muted-foreground">Link each line to the material it's for — its RFQ/order/delivery dates will then fill in automatically.</p>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="space-y-1 rounded-md border border-transparent p-1 hover:border-border" data-testid={`material-indent-item-row-${idx}`}>
                  <div className="flex items-center gap-2">
                    <ItemPicker required value={it.description} onChange={(v) => updateItem(idx, "description", v)} onPick={(item) => pickItem(idx, item)} />
                    <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} />
                    <Input type="number" step="0.01" placeholder="Est. rate" className="w-28" value={it.estimated_rate} onChange={(e) => updateItem(idx, "estimated_rate", e.target.value)} />
                    {items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                  </div>
                  <div className="flex items-center gap-2 pl-0.5">
                    <span className="w-20 shrink-0 text-[11px] text-muted-foreground">Material</span>
                    <Select value={it.wbs_item_id || "none"} onValueChange={(v) => updateItem(idx, "wbs_item_id", v === "none" ? "" : v)}>
                      <SelectTrigger className="h-9 max-w-xs flex-1"><SelectValue placeholder="Not linked" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="none">Not linked to a material</SelectItem>
                        {leafItems.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_INDENT_ITEM }])}><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <div><Label>Remarks</Label><Textarea value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MaterialDialog({ projectId, item, onClose, onSaved }) {
  const d = item.material || {};
  const [form, setForm] = useState({
    priority: d.priority || "", qty_rfq: d.qty_rfq ?? "", qty_ordered: d.qty_ordered ?? "", qty_received: d.qty_received ?? "",
    rfq_date: d.rfq_date || "", planned_order_date: d.planned_order_date || "", planned_mfc_date: d.planned_mfc_date || "", planned_delivery_date: d.planned_delivery_date || "",
    loi_date: d.loi_date || "", order_no: d.order_no || "", actual_order_date: d.actual_order_date || "", actual_mfc_date: d.actual_mfc_date || "",
    unpriced_po_date: d.unpriced_po_date || "", outlook_delivery_date: d.outlook_delivery_date || "", lead_time_days: d.lead_time_days ?? "",
    current_status: d.current_status || "", supplier: d.supplier || "", inspection_date: d.inspection_date || "", mdcc_date: d.mdcc_date || "",
    dispatch_date: d.dispatch_date || "", actual_delivery_date: d.actual_delivery_date || "", remarks: d.remarks || "", entry_date: todayISO(),
  });
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const dateFields = ["rfq_date", "planned_order_date", "planned_mfc_date", "planned_delivery_date", "loi_date", "actual_order_date", "actual_mfc_date", "unpriced_po_date", "outlook_delivery_date", "inspection_date", "mdcc_date", "dispatch_date", "actual_delivery_date"];
  const numFields = ["qty_rfq", "qty_ordered", "qty_received", "lead_time_days"];

  const submit = async () => {
    try {
      const payload = { ...form };
      dateFields.forEach((f) => { payload[f] = payload[f] || null; });
      numFields.forEach((f) => { payload[f] = payload[f] === "" ? null : Number(payload[f]); });
      await api.patch(`/projects/${projectId}/materials/${item.id}`, payload);
      toast.success("Material status updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="material-dialog">
        <DialogHeader><DialogTitle>{item.code} — {item.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Priority</Label><Input data-testid="mat-priority-input" value={form.priority} onChange={set("priority")} className="mt-1.5" placeholder="Long Lead / Critical" /></div>
          <div><Label>Supplier</Label><Input data-testid="mat-supplier-input" value={form.supplier} onChange={set("supplier")} className="mt-1.5" /></div>
          <div><Label>Qty RFQ'd</Label><Input type="number" data-testid="mat-qty-rfq-input" value={form.qty_rfq} onChange={set("qty_rfq")} className="mt-1.5" /></div>
          <div><Label>Qty ordered</Label><Input type="number" data-testid="mat-qty-ordered-input" value={form.qty_ordered} onChange={set("qty_ordered")} className="mt-1.5" /></div>
          <div><Label>Qty received</Label><Input type="number" data-testid="mat-qty-received-input" value={form.qty_received} onChange={set("qty_received")} className="mt-1.5" /></div>
          <div><Label>Lead time (days)</Label><Input type="number" data-testid="mat-lead-time-input" value={form.lead_time_days} onChange={set("lead_time_days")} className="mt-1.5" /></div>

          <p className="col-span-2 mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Planned (L4)</p>
          <div><Label>RFQ date</Label><Input type="date" data-testid="mat-rfq-date-input" value={form.rfq_date} onChange={set("rfq_date")} className="mt-1.5" /></div>
          <div><Label>Planned order date</Label><Input type="date" data-testid="mat-planned-order-date-input" value={form.planned_order_date} onChange={set("planned_order_date")} className="mt-1.5" /></div>
          <div><Label>Planned MFC date</Label><Input type="date" data-testid="mat-planned-mfc-date-input" value={form.planned_mfc_date} onChange={set("planned_mfc_date")} className="mt-1.5" /></div>
          <div><Label>Planned delivery date</Label><Input type="date" data-testid="mat-planned-delivery-date-input" value={form.planned_delivery_date} onChange={set("planned_delivery_date")} className="mt-1.5" /></div>

          <p className="col-span-2 mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actual status</p>
          <div><Label>LOI date</Label><Input type="date" data-testid="mat-loi-date-input" value={form.loi_date} onChange={set("loi_date")} className="mt-1.5" /></div>
          <div><Label>Order no.</Label><Input data-testid="mat-order-no-input" value={form.order_no} onChange={set("order_no")} className="mt-1.5" /></div>
          <div><Label>Actual order date</Label><Input type="date" data-testid="mat-actual-order-date-input" value={form.actual_order_date} onChange={set("actual_order_date")} className="mt-1.5" /></div>
          <div><Label>Actual MFC date</Label><Input type="date" data-testid="mat-actual-mfc-date-input" value={form.actual_mfc_date} onChange={set("actual_mfc_date")} className="mt-1.5" /></div>
          <div><Label>Unpriced PO date</Label><Input type="date" data-testid="mat-unpriced-po-date-input" value={form.unpriced_po_date} onChange={set("unpriced_po_date")} className="mt-1.5" /></div>
          <div><Label>Outlook delivery date</Label><Input type="date" data-testid="mat-outlook-delivery-date-input" value={form.outlook_delivery_date} onChange={set("outlook_delivery_date")} className="mt-1.5" /></div>
          <div><Label>Inspection date</Label><Input type="date" data-testid="mat-inspection-date-input" value={form.inspection_date} onChange={set("inspection_date")} className="mt-1.5" /></div>
          <div><Label>MDCC date</Label><Input type="date" data-testid="mat-mdcc-date-input" value={form.mdcc_date} onChange={set("mdcc_date")} className="mt-1.5" /></div>
          <div><Label>Dispatch date</Label><Input type="date" data-testid="mat-dispatch-date-input" value={form.dispatch_date} onChange={set("dispatch_date")} className="mt-1.5" /></div>
          <div><Label>Actual delivery date</Label><Input type="date" data-testid="mat-actual-delivery-date-input" value={form.actual_delivery_date} onChange={set("actual_delivery_date")} className="mt-1.5" /></div>
          <div><Label>Current status</Label><Input data-testid="mat-current-status-input" value={form.current_status} onChange={set("current_status")} className="mt-1.5" /></div>
          <div className="col-span-2"><Label>Remarks</Label><Textarea data-testid="mat-remarks-input" value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <div><Label>Back-date entry</Label><Input type="date" data-testid="mat-entry-date-input" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="material-submit-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
